import { ProtocolError, SandboxError } from './errors';
import type { FmtResponse, RunRequest, RunResponse, RunResult } from './types';

export const MAX_CODE_BYTES = 65_536;
export const MAX_STDIN_BYTES = 1_048_576;
export const MAX_CASES = 10;

const encoder = new TextEncoder();

/** UTF-8 のバイト長。 */
export function byteLength(s: string): number {
  return encoder.encode(s).length;
}

/**
 * 送信前検査（sandboxd-client.md §4.1）。違反なら sandboxd と同じ文言の `rejected` を投げる。
 */
export function checkRunRequest(req: RunRequest): void {
  checkCode(req.code);
  if (!Array.isArray(req.stdins) || req.stdins.length < 1 || req.stdins.length > MAX_CASES) {
    throw new SandboxError('rejected', null, 'テストケース数が不正です');
  }
  for (const s of req.stdins) {
    if (typeof s !== 'string' || byteLength(s) > MAX_STDIN_BYTES) {
      throw new SandboxError('rejected', null, '標準入力が大きすぎます (1MiB まで)');
    }
  }
}

/** `/api/fmt` の送信前検査（§2.2）。 */
export function checkCode(code: string): void {
  if (typeof code !== 'string' || code.trim() === '') {
    throw new SandboxError('rejected', null, 'コードが空です');
  }
  if (byteLength(code) > MAX_CODE_BYTES) {
    throw new SandboxError('rejected', null, 'コードが大きすぎます (64KiB まで)');
  }
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function toRunResult(v: unknown, i: number): RunResult {
  if (!isRecord(v)) throw new ProtocolError(`runs[${i}] is not an object`);
  const { stdout, stderr, exitCode, timedOut, durationMs } = v;
  if (typeof stdout !== 'string') throw new ProtocolError(`runs[${i}].stdout is not a string`);
  if (typeof stderr !== 'string') throw new ProtocolError(`runs[${i}].stderr is not a string`);
  if (typeof exitCode !== 'number' || !Number.isInteger(exitCode)) {
    throw new ProtocolError(`runs[${i}].exitCode is not an integer`);
  }
  if (typeof timedOut !== 'boolean')
    throw new ProtocolError(`runs[${i}].timedOut is not a boolean`);
  // durationMs は判定に使わないため、数値でなければ 0 とみなす（judge.md §3.4 の検査対象外）。
  const ms = typeof durationMs === 'number' && Number.isFinite(durationMs) ? durationMs : 0;
  return { stdout, stderr, exitCode, timedOut, durationMs: ms };
}

/**
 * `/api/run` の 200 本文を契約どおりか検査し、型付きで返す（judge.md §3.4）。
 * 合わなければ {@link ProtocolError}。
 */
export function validateRunResponse(body: unknown, caseCount: number): RunResponse {
  if (!isRecord(body)) throw new ProtocolError('response is not an object');
  const compile = body['compile'];
  if (!isRecord(compile) || typeof compile['ok'] !== 'boolean') {
    throw new ProtocolError('compile.ok is missing');
  }
  const out: RunResponse = { compile: { ok: compile['ok'] } };
  if (typeof compile['output'] === 'string' && out.compile) out.compile.output = compile['output'];
  if (typeof body['error'] === 'string') out.error = body['error'];
  if (compile['ok']) {
    const runs = body['runs'];
    if (!Array.isArray(runs)) throw new ProtocolError('runs is not an array');
    if (runs.length !== caseCount) {
      throw new ProtocolError(`runs.length ${runs.length} !== cases ${caseCount}`);
    }
    out.runs = runs.map(toRunResult);
  }
  return out;
}

/** `/api/fmt` の 200 本文を検査する（§2.2）。 */
export function validateFmtResponse(body: unknown): FmtResponse {
  if (!isRecord(body) || typeof body['ok'] !== 'boolean') {
    throw new ProtocolError('fmt ok is missing');
  }
  const out: FmtResponse = { ok: body['ok'] };
  if (typeof body['code'] === 'string') out.code = body['code'];
  if (typeof body['error'] === 'string') out.error = body['error'];
  if (out.ok && out.code === undefined) throw new ProtocolError('fmt code is missing');
  return out;
}
